import { useMemo } from "react";

import { isPresent } from "neetocist";
import { DEFAULT_PAGE_INDEX } from "neetocommons/constants";
import { useQueryParams } from "neetocommons/react-utils";
import { NoData, Spinner, Pagination } from "neetoui";
import propTypes from "prop-types";
import { useTranslation } from "react-i18next";

import Container from "components/Container";
import Header from "components/Header";

import Card from "./Card";
import { AUDIT_LOGS_PAGE_SIZE } from "./constants";
import { useFetchAuditLogs } from "./useAuditLogsApi";
import { useFetchCountries } from "./useCountriesApi";
import { useFetchPermissions } from "./usePermissionsApi";
import { indexById, indexByPermissionName } from "./utils";

const AuditLogs = ({ headerProps, noDataHelpText, noDataTitle }) => {
  const { t } = useTranslation();

  const { page = DEFAULT_PAGE_INDEX } = useQueryParams();

  const {
    data: { auditLogs = [], totalCount = 0 } = {},
    isLoading,
    isFetching,
  } = useFetchAuditLogs({ params: { page, pageSize: AUDIT_LOGS_PAGE_SIZE } });

  const { data: countriesData } = useFetchCountries();

  const { data: permissionsData } = useFetchPermissions();

  const countriesList = countriesData?.countries;
  const permissionsList = permissionsData?.permissions;

  const permissionsMap = useMemo(
    () => (permissionsList ? indexByPermissionName(permissionsList) : {}),
    [permissionsList]
  );

  const countriesMap = useMemo(
    () => (countriesList ? indexById(countriesList) : {}),
    [countriesList]
  );

  if (isLoading || isFetching) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <Container isHeaderFixed className="overflow-hidden">
      <Header
        className="px-5 md:px-10"
        title={t("neetoMolecules.auditLogs.title")}
        {...headerProps}
      />
      {isPresent(auditLogs) ? (
        <div className="w-full grow overflow-y-auto px-5 md:px-10">
          {auditLogs.map((auditLog, index) => (
            <Card
              {...{ auditLog, countriesMap, index, permissionsMap }}
              key={auditLog.id}
              totalCount={auditLogs.length}
            />
          ))}
        </div>
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <NoData
            helpText={noDataHelpText}
            title={noDataTitle || t("neetoMolecules.auditLogs.noData")}
          />
        </div>
      )}
      {totalCount > 0 && (
        <div className="flex w-full justify-end px-5 py-4 md:px-10">
          <Pagination
            count={totalCount}
            pageNo={parseInt(page)}
            pageSize={AUDIT_LOGS_PAGE_SIZE}
          />
        </div>
      )}
    </Container>
  );
};

AuditLogs.propTypes = {
  /**
   * Props to be passed to the `Header` component.
   */
  headerProps: propTypes.object,
  /**
   * Help text to be displayed to the `NoData` component when there are no audit logs.
   */
  noDataHelpText: propTypes.string,
  /**
   * Title to be passed to the `NoData` component when there are no audit logs.
   */
  noDataTitle: propTypes.string,
};

export default AuditLogs;
